/**
 * Como a amostra aparece — onda 303 (RN-248).
 *
 * «Outra questão é inverter, deixar a nomenclatura da amostra do cliente em
 *  evidência e a numeração única da amostra no lugar. Isso para o sistema
 *  inteiro» (Humberto, 08/10/2026).
 *
 * Em TODO lugar, a amostra é chamada pelo nome que o cliente deu a ela
 * («SP-01/2», «SH-01»): é o que está escrito no saco, o que o laudo cita e o
 * que todo mundo fala. O código AM — o único, que nunca muda e é o do QR —
 * vem logo depois, menor, e nunca some: dois clientes podem mandar uma
 * «SP-01/1», mas não existem duas AM-2026-00202.
 *
 * Onde a tela mistura amostras de várias OS (fila, estufa, busca, lista de
 * amostras, máquinas, descarte), a OS vai junto do código — «SP-01/1» sozinho
 * não diz de qual obra é. Dentro de uma OS a identificação não se repete: o
 * recebimento barra (`fn_amostra_identificacao_unica_na_os`, 0249).
 *
 * Amostra sem identificação do cliente (a que chegou sem nome) aparece pelo
 * código, como antes.
 *
 * Use SEMPRE estas funções para mostrar uma amostra — nunca `a.codigo` solto.
 */
import type { ReactNode } from 'react';

export interface AmostraComNome {
  /** O código único, AM-AAAA-NNNNN. Pode faltar na prevista (nasce na chegada). */
  codigo?: string | null;
  /** O nome que o cliente deu (`amostra.identificacao_recebida`). */
  identificacao?: string | null;
  /** O número da OS, para as telas que misturam OS. */
  os?: string | null;
}

const limpo = (v?: string | null) => (v ?? '').trim();

/** O nome principal: a identificação do cliente; sem ela, o código. */
export function tituloDaAmostra(a: AmostraComNome): string {
  return limpo(a.identificacao) || limpo(a.codigo) || 'amostra sem nome';
}

/**
 * A segunda linha: o código (e a OS, quando pedida). Vazia quando o título
 * já é o código e não há OS.
 */
export function apoioDaAmostra(a: AmostraComNome): string {
  const temIdent = !!limpo(a.identificacao);
  return [
    temIdent ? limpo(a.codigo) || null : null,
    limpo(a.os) ? `OS ${limpo(a.os)}` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * Numa linha de texto (título de janela, `aria-label`, opção de lista,
 * mensagem): «SP-01/2 · AM-2026-00202» (e «· OS 2610412» quando há OS).
 */
export function nomeDaAmostra(a: AmostraComNome): string {
  const apoio = apoioDaAmostra(a);
  return apoio ? `${tituloDaAmostra(a)} · ${apoio}` : tituloDaAmostra(a);
}

/**
 * Atalho para os objetos que vêm da API com os nomes de sempre
 * (`codigo`/`amostra_codigo` e `identificacao_recebida`/`amostra_identificacao`).
 */
export function daApi(a: {
  codigo?: string | null; amostra_codigo?: string | null;
  identificacao_recebida?: string | null; amostra_identificacao?: string | null;
  os_numero?: string | null;
}, comOs = false): AmostraComNome {
  return {
    codigo: a.amostra_codigo ?? a.codigo ?? null,
    identificacao: a.amostra_identificacao ?? a.identificacao_recebida ?? null,
    os: comOs ? a.os_numero ?? null : null,
  };
}

/**
 * O nome na tela. Duas linhas por padrão (o nome do cliente em destaque e o
 * código embaixo, menor); `emLinha` põe as duas na mesma linha, para chips,
 * títulos de cartão e células apertadas.
 */
export function NomeDaAmostra({ amostra, emLinha, forte = true, depois }: {
  amostra: AmostraComNome;
  emLinha?: boolean;
  /** O nome em negrito (padrão). Em título `<h1>` o próprio título já pesa. */
  forte?: boolean;
  /** Algo logo depois do nome (um selo, um qualificador). */
  depois?: ReactNode;
}) {
  const titulo = tituloDaAmostra(amostra);
  const apoio = apoioDaAmostra(amostra);
  return (
    <span className={`nome-amostra${emLinha ? ' em-linha' : ''}`}
      data-codigo={limpo(amostra.codigo) || undefined}>
      <span className={`nome-amostra-titulo${forte ? ' forte' : ''}`}>{titulo}</span>
      {depois}
      {apoio && <span className="nome-amostra-codigo">{apoio}</span>}
    </span>
  );
}
